import { Button, Checkbox, Col, Form, Modal, Row, Select, Tabs, Tooltip } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { CheckCircle, ChevronDown, ChevronUp, CloseIcon, Information } from '../../assets/icons';
import FloatingLabel from '../../components/FloatingLabel/FloatingLabel';
import BankAccountModal from '../../components/LinkBankAccount/BankAccountModal';
import ListingAgreement from '../../components/ListingAgreement/ListingAgreement';
import PinModalWithoutButton from '../../components/PinModalWithoutButton/PinModalWithoutButton';
import BuyOrder from '../../components/SecondaryTrade/BuyOrder';
import SellOrder from '../../components/SecondaryTrade/SellOrder';
import SuccessModal from '../../components/SuccessModal/SuccessModal';
import { Images } from '../../config/images';
import useMediaQuery from '../../Hooks/useMediaQuery';
import useSecondaryMarketEligibility from '../../Hooks/useSecondaryMarketEligibility';
import { formatePhoneNumber } from '../../utils/formatPhoneNumber';
import { getBankLogo } from '../../utils/getBankLogo';
import { getUsCurrencyFormat } from '../../utils/usCurrencyFormatter';
import ResetPin from '../Account/Profile/Modals/ResetPin';
import { BuyReviewData, BuySuccessData, SellReviewData, SellSuccessData } from './TradeData';
import { secondaryBuy } from '../../constants/secondaryBuy';
import { BankVerificationPendingModal } from '../../components/Instances/BankVerificationPendingModal';
import { NoFundModal } from '../../components/Instances/NoFundModal';
import { PendingTransferModal } from '../../components/Instances/PendingTransferModal';
import TransferMoreFunds from '../../components/Instances/TransferMoreFunds';
import SecondaryMarketAccessModal from '../../components/SecondaryMarketAccessModal/SecondaryMarketAccessModal';
import { TOOLTIP_TEXT } from '../../utils/constant';
import { computeSecondaryTradeTabState } from '../../utils/secondaryTradeTabState';

const handleKeyActivate = (callback) => (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    callback(e);
  }
};

const TradeModal = (props) => {
  const { quickBuyPropertyData, isSingleTab, isSell, featchData, quickBuySecondaryOptions, isTradeFlow } = props;
  const [isScrolledBuy] = useState(false);
  const [shareValue, setShareValue] = useState();
  const [sellPrice, setSellPrice] = useState();
  const [, setReviewData] = useState(BuyReviewData);
  const [successData, setSuccessData] = useState(BuySuccessData);
  const [transferFundModal,setTransferFundModal]=useState(false);
  const [bankModalOpen, setBankModalOpen] = useState(false);
  const [bankOpen, setBankOpen] = useState(false);
  const loading = useSelector((state) => state.secondaryPlaceBid.loading);
  const orderRef = useRef();
  const userProfileData = useSelector((state) => state.userData.userData);
  const isSellOrdersTradable = useSelector((state) => state.propertyDetails?.data?.data?.isSellOrdersTradable === true);
  const { isBuyEligible, buyIneligibleMsg, isSellEligible, sellIneligibleMsg, isEntityRestricted } = useSecondaryMarketEligibility();

  const phoneNumber = ['+1', formatePhoneNumber(userProfileData?.phoneNumber)];
  const linkText = 'My Account>Account Balances';
  const linkPath = '/account/transactions';
  const openLink = () => {
    window.location.href = linkPath;
  };
  const {
    pin,
    resetPin,
    loading: hookLoading,
    numberOfShare,
    isMaxRangeError,
    isMinRangeError,
    minButtonValue,
    sharesRemainingToPurchase,
    limitPrice,
    LowestAskPrice,
    highestBid,
    duration,
    totalInvestment,
    transactionFee,
    totalPurchase,
    isEditPayment,
    walletsData,
    selectedPaymentMethod,
    isTurmAndCondistion,
    partialFills,
    perSharePrice,
    userPurchasedShare,
    activeTab,
    setActiveTab,
    setPartialFills,
    setPin,
    handleVerifyPin,
    handleForgotPin,
    setResetPin,
    setNumberOfShare,
    setLimitPrice,
    setDuration,
    setIsEditPayment,
    handleSelectPaymentMethod,
    handleAddBank,
    setIsTurmAndCondistion,
    handlePlaceBid,
    handleSellOrder,
    setLoading,
    isTOSModal,
    onTOSAgreementSuccess,
    onTOSModalClose,
    handleAfterAgreementAccepted,
    sharesRemainingToPurchaseForSecondary,
    propertyDetails,
  } = quickBuySecondaryOptions;

  const { sellTabDisabled, sellTabTooltip, buyTabDisabled, buyTabTooltip } = computeSecondaryTradeTabState({
    userPurchasedShare,
    isSellOrdersTradable,
    isSellEligible,
    sellIneligibleMsg,
    isTradeFlow,
    isBuyEligible,
    buyIneligibleMsg,
    isEntityRestricted,
  });

  const remainingOpenShares = Number(propertyDetails?.data?.userPurchaseDetail?.remainingOpenShares || 0);
  const isBuyOrdersPending = propertyDetails?.data?.userPurchaseDetail?.isBuyOrdersPending;

  function handleSellClick() {
    setReviewData(SellReviewData);
    setSuccessData(SellSuccessData);
    props.modalState('StepThree');
  }

  useEffect(() => {
    // Sync activeTab whenever the modal opens (StepOne) OR isSell changes.
    // Using props.open ensures we re-sync even if isSell was already '2'
    // from a previous sell click (in which case isSell doesn't change, so
    // a [isSell]-only effect would not re-fire).
    // This must stay tied to the clicked tab (isSell) — the PIN-verify
    // eligibility toast (useQuickBuySecondary) reads activeTab right after
    // this to decide whether to block, so it must reflect the button the
    // user actually clicked, not a pre-emptively swapped tab.
    if (props.open === 'StepOne') {
      setActiveTab(isSell);
    }
  }, [props.open, isSell]);

  useEffect(() => {
    // Once the tabs modal itself is showing (StepTwo, i.e. eligibility has
    // already been resolved), fall back to the other tab if the one we
    // landed on turns out to be disabled — e.g. the Trade button always
    // opens on Buy, but Buy may be ineligible while Sell is not.
    if (props.open !== 'StepTwo') return;
    if (activeTab === '1' && buyTabDisabled && !sellTabDisabled) {
      setActiveTab('2');
    } else if (activeTab === '2' && sellTabDisabled && !buyTabDisabled) {
      setActiveTab('1');
    }
  }, [props.open, activeTab, buyTabDisabled, sellTabDisabled]);
  const openTransferModal = useCallback(() => {
    setTransferFundModal(true);
    props.modalState('');
  }, []);
  const screenMD = useMediaQuery('(min-width: 1000px)');
  const addBankModalOpen = useMemo(() => bankModalOpen || secondaryBuy.ADD_BANK === props.open, [bankModalOpen, props.open]);
  return (
    <>
      {/* === vPIN Modal Starts Here === */}
      {screenMD && (
        <>
          {props.open === 'StepOne' && (
            <PinModalWithoutButton
              onCancel={() => {
                props.modalState();
                setPin('');
              }}
              open={props.open === 'StepOne'}
              pin={pin}
              loading={hookLoading}
              handleForgotPin={handleForgotPin}
              setPin={setPin}
              setLoading={setLoading}
              onSuccess={(isTOSAccepted) => {
                orderRef?.current?.resetFields();
                handleVerifyPin(isTOSAccepted);
              }}
            />
          )}
        </>
      )}

      {/* === PIN Modal Ends Here === */}

      {/* === Buy / Sell Order Modal Starts Here === */}
      <ResetPin phoneNumber={phoneNumber} mail={userProfileData?.email} setOpen={resetPin} setClose={() => setResetPin(false)} />
      <BankAccountModal
        bankModalOpen={addBankModalOpen}
        setBankModalOpen={(value) => {
          if (value) {
            props.modalState(secondaryBuy.ADD_BANK);
          } else {
            props.modalState('');
          }
          setBankModalOpen(value);
        }}
        onSuccess={(value) => handleAddBank(value)}
        onClose={() => setIsEditPayment(false)}
      />
      <BankVerificationPendingModal
        open={props.open === secondaryBuy.VERIFY_BANK}
        onClose={() => props.modalState('')}
        onAddNewBank={() => props.modalState(secondaryBuy.ADD_BANK)}
      />
      <NoFundModal open={props.open === secondaryBuy.NO_MONEY} onClose={() => props.modalState('')} openTransferFunds={openTransferModal} />
      <PendingTransferModal
        open={props.open === secondaryBuy.PENDING_TRANSFER}
        onClose={() => props.modalState('')}
        openTransferFunds={openTransferModal}
      />
      <TransferMoreFunds open={transferFundModal} onOpen={openTransferModal} onClose={() => setTransferFundModal(false)} />
      
      {/* === Secondary Market Access Modal === */}
      <SecondaryMarketAccessModal
        visible={props.open === 'AccessModal' || props.open === 'AccessModal_Identity' || props.open === 'AccessModal_Agreement'}
        subStep={props.open}
        modalState={props.modalState}
        onRemindLater={() => props.modalState()}
        onAgreementSuccess={handleAfterAgreementAccepted}
        identificationDocs={userProfileData?.identificationDocs}
      />
      
      {screenMD && (
        <>
          {props.open === 'StepTwo' && (
            <Modal
              className='quickbuy buy-order ant-modal--footer-shadow ant-modal--both'
              open={props.open === 'StepTwo'}
              // onCancel={() => props.modalState()}
              title={
                <div className={isScrolledBuy ? 'buy-order-header scrolled' : 'buy-order-header'}>
                  <div className='header__img'>
                    {quickBuyPropertyData?.images?.image1 ? (
                      <LazyLoadImage
                        onError={(element) => {
                          element.target.src = Images.noPropertyFoundImage;
                        }}
                        src={quickBuyPropertyData?.images?.image1}
                        alt='house with garden'
                      />
                    ) : (
                      <LazyLoadImage src={Images.noPropertyFoundImage} alt='the-leonardo' />
                    )}
                  </div>
                  <div className='header__content'>
                    <p className='subhead-2 text-copper mb-0'>{quickBuyPropertyData?.propertyName || ''}</p>
                    <div className='name-type'>
                      <span className='fz-12'>{`${quickBuyPropertyData?.cityName || ''}, ${quickBuyPropertyData?.stateName || ''}`}</span>
                      <span className='fz-12 ml-4 mr-4 text-taupe-60'>•</span>
                      <span className='fz-12'>{quickBuyPropertyData?.propertyType || ''}</span>
                    </div>
                    <div className='share-details mt-12'>
                      <div className='block'>
                        <span className='text-taupe-80'>Last price</span>
                        <span className='subhead-3'>{`$${getUsCurrencyFormat(quickBuyPropertyData?.lastTradedPrice || 0)}`}</span>
                      </div>
                      <div className='block'>
                        <span className='text-taupe-80'>
                          All time
                          <Tooltip placement='bottom' title='All Time reflects the total change in share price since the initial offering'>
                            <span>
                              <Information className='cursor-pointer' verticalAlign='middle' marginLeft='4px' color='#1A1519' />
                            </span>
                          </Tooltip>
                        </span>
                        <span className='subhead-3'>
                          {`${getUsCurrencyFormat(quickBuyPropertyData?.allTime) > 0 ? '+' : ''} ${getUsCurrencyFormat(
                            (quickBuyPropertyData?.allTime || '').toString().replace(',', '') || 0
                          )}%`}
                        </span>
                      </div>
                      <div className='block'>
                        <span className='text-taupe-80'>
                          Est. Dividend Yield
                          <Tooltip
                            placement='bottom'
                            popupClassName='div-yeild-pop'
                            title={TOOLTIP_TEXT.ESTIMATED_DIVIDEND_YIELD}
                          >
                            <span>
                              <Information className='cursor-pointer' verticalAlign='middle' marginLeft='4px' color='#1A1519' />
                            </span>
                          </Tooltip>
                        </span>
                        <span className='subhead-3'>{quickBuyPropertyData?.dividendYield ?? 'NA'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              }
              footer={
                <>
                  {activeTab === '1' && (
                    <Tooltip title={buyTabTooltip}>
                      <span>
                        <Button
                          type='primary'
                          className='w-100 mt-8 mb-8'
                          onClick={() => props.modalState('StepThree')}
                          disabled={
                            buyTabDisabled ||
                            !numberOfShare ||
                            isMaxRangeError ||
                            Number(numberOfShare) % 1 !== 0 ||
                            !/^(?!0\d)(\d+(?:\.\d{1,2})?)$/.test(limitPrice) ||
                            (limitPrice && Number(limitPrice) < 0.01) ||
                            (limitPrice && Number(limitPrice) > 10000) ||
                            totalPurchase <= 0 ||
                            totalPurchase < 10 ||
                            totalPurchase > 10000
                          }
                        >
                          Preview order
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  {activeTab === '2' && (
                    <Tooltip title={sellTabTooltip}>
                      <span>
                        <Button
                          disabled={
                            sellTabDisabled ||
                            !numberOfShare ||
                            numberOfShare > userPurchasedShare ||
                            Number(numberOfShare) % 1 !== 0 ||
                            !/^(?!0\d)(\d+(?:\.\d{1,2})?)$/.test(limitPrice) ||
                            Number(limitPrice) < 0.01 ||
                            Number(limitPrice) > 10000 ||
                            totalPurchase <= 0 ||
                            totalPurchase < 10 ||
                            totalPurchase > 10000
                          }
                          type='primary'
                          className='w-100 mt-8 mb-8'
                          onClick={handleSellClick}
                        >
                          Preview order
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </>
              }
              width={524}
              closeIcon={<CloseIcon onClick={() => props.modalState()} />}
              centered
            >
              {isSingleTab ? (
                <BuyOrder
                  ref={orderRef}
                  numberOfShare={numberOfShare}
                  isMaxRangeError={isMaxRangeError}
                  isMinRangeError={isMinRangeError}
                  minButtonValue={minButtonValue}
                  sharesRemainingToPurchase={sharesRemainingToPurchase}
                  setNumberOfShare={setNumberOfShare}
                  limitPrice={limitPrice}
                  setLimitPrice={setLimitPrice}
                  LowestAskPrice={LowestAskPrice}
                  duration={duration}
                  quickBuyPropertyData={quickBuyPropertyData}
                  setDuration={setDuration}
                  totalInvestment={totalInvestment}
                  transactionFee={transactionFee}
                  totalPurchase={getUsCurrencyFormat(totalPurchase)}
                  rawTotalPurchase={totalPurchase}
                  partialFills={partialFills}
                  setPartialFills={setPartialFills}
                  sharesRemainingToPurchaseForSecondary={sharesRemainingToPurchaseForSecondary}
                  minimumPrice={propertyDetails?.data?.minimumPrice}
                />
              ) : (
                <Tabs
                  className='mt-24'
                  activeKey={activeTab}
                  onChange={(key) => {
                    setActiveTab(key);
                    setNumberOfShare('');
                    setLimitPrice('');
                    setDuration('Good Until Cancel');
                    orderRef.current.resetFields();
                  }}
                  items={[
                    {
                      label: buyTabDisabled ? (
                        <Tooltip title={buyTabTooltip}>
                          <span className='trade-tab-label'>Buy</span>
                        </Tooltip>
                      ) : <span className='trade-tab-label'>Buy</span>,
                      key: '1',
                      disabled: buyTabDisabled,
                      children: (
                        <BuyOrder
                          ref={orderRef}
                          numberOfShare={numberOfShare}
                          isMaxRangeError={isMaxRangeError}
                          isMinRangeError={isMinRangeError}
                          minButtonValue={minButtonValue}
                          sharesRemainingToPurchase={sharesRemainingToPurchase}
                          setNumberOfShare={setNumberOfShare}
                          limitPrice={limitPrice}
                          setLimitPrice={setLimitPrice}
                          LowestAskPrice={LowestAskPrice}
                          duration={duration}
                          quickBuyPropertyData={quickBuyPropertyData}
                          setDuration={setDuration}
                          totalInvestment={totalInvestment}
                          transactionFee={transactionFee}
                          totalPurchase={getUsCurrencyFormat(totalPurchase)}
                          rawTotalPurchase={totalPurchase}
                          partialFills={partialFills}
                          setPartialFills={setPartialFills}
                          sharesRemainingToPurchaseForSecondary={sharesRemainingToPurchaseForSecondary}
                          minimumPrice={propertyDetails?.data?.minimumPrice}
                        />
                      ),
                    },
                    {
                      label: sellTabDisabled ? (
                        <Tooltip title={sellTabTooltip}>
                          <span className='trade-tab-label'>Sell</span>
                        </Tooltip>
                      ) : <span className='trade-tab-label'>Sell</span>,
                      key: '2',
                      disabled: sellTabDisabled,
                      children: (
                        <>
                          <SellOrder
                            ref={orderRef}
                            shareValue={shareValue}
                            setShareValue={setShareValue}
                            sellPrice={sellPrice}
                            setSellPrice={setSellPrice}
                            duration={duration}
                            setDuration={setDuration}
                            userPurchasedShare={userPurchasedShare}
                            remainingOpenShares={remainingOpenShares}
                            isBuyOrdersPending={isBuyOrdersPending}
                            numberOfShare={numberOfShare}
                            setNumberOfShare={setNumberOfShare}
                            limitPrice={limitPrice}
                            setLimitPrice={setLimitPrice}
                            highestBid={highestBid}
                            quickBuyPropertyData={quickBuyPropertyData}
                            totalInvestment={totalInvestment}
                            transactionFee={transactionFee}
                            totalPurchase={getUsCurrencyFormat(totalPurchase)}
                            partialFills={partialFills}
                            setPartialFills={setPartialFills}
                            minimumPrice={propertyDetails?.data?.minimumPrice}
                            rawTotalPurchase={totalPurchase}
                          />
                        </>
                      ),
                    },
                  ]}
                />
              )}
            </Modal>
          )}
        </>
      )}

      {/* === Buy / Sell Order Modal Ends Here === */}

      {/* === Order Review Modal Starts Here ===  */}
      {screenMD && (
        <Modal
          className='quickbuy review-buy-order ant-modal--footer-shadow ant-modal--footer'
          open={props.open === 'StepThree'}
          // onCancel={() => props.modalState()}
          width={524}
          closeIcon={<CloseIcon onClick={() => props.modalState()} />}
          footer={
            <>
              <Row gutter={16} className='mt-8 mb-8'>
                <Col span={12}>
                  <Button className='w-100' onClick={() => props.modalState('StepTwo')}>
                    Back
                  </Button>
                </Col>
                <Col span={12}>
                  <Button
                    loading={loading}
                    disabled={!isTurmAndCondistion}
                    type='primary'
                    className='w-100'
                    onClick={() => (activeTab === '1' ? handlePlaceBid() : handleSellOrder())}
                  >
                    Place order
                  </Button>
                </Col>
              </Row>
            </>
          }
          centered
        >
          {activeTab === '1' ? (
            <>
              <h4 className='text-center mb-24'>Review buy order</h4>
              <p className='fw-600'>Investment details</p>
              <div className='d-flex flex-column g-16 fz-14'>
                <div className='d-flex align-items-center justify-content-space-between'>
                  <span className='text-taupe-80'>Shares</span>
                  <span>{numberOfShare || ''}</span>
                </div>
                <div className=' d-flex align-items-center justify-content-space-between'>
                  <span className='text-taupe-80'>Price per share</span>
                  <span>{`$${getUsCurrencyFormat(limitPrice || '')}`}</span>
                </div>
                <div className=' d-flex align-items-center justify-content-space-between'>
                  <span className='text-taupe-80'>Total investment</span>
                  <span>{`$${getUsCurrencyFormat(totalInvestment || '')}`}</span>
                </div>
                <div className=' d-flex align-items-center justify-content-space-between'>
                  <span className='text-taupe-80'>Platform fee </span>
                  <span>{`$2.50`}</span>
                </div>
                <div className=' d-flex align-items-center justify-content-space-between'>
                  <span className='text-taupe-80'>Broker-Dealer/ATS Fee (1%)</span>
                  <span>{`$${transactionFee || ''}`}</span>
                </div>
              </div>
              <hr className='mb-16 mt-16' />
              <div className='d-flex flex-column g-16 fz-14'>
                <div className='d-flex flex-column g-16 fz-14'>
                  <div className='d-flex align-items-center justify-content-space-between'>
                    <span className='text-taupe-80'>Total purchase (USD)</span>
                    <span className='fw-600'>{`$${getUsCurrencyFormat(totalPurchase || '')}`}</span>
                  </div>
                  {/* Duration field commented out - Good Until Cancel / Good for the Day */}
                  {/*                   <div className='d-flex align-items-center justify-content-space-between'>
                    <Tooltip
                      title={
                        <>
                          <p>
                            A “good until cancel” order is a buy or sell order that remains active until it is either executed or until the investor
                            cancels it.
                          </p>
                          <p className='mb-0'>
                            A “good for the day” order is a buy or sell order that is only good for the remainder of the trading day on which it is
                            placed. All “good for the day” orders will expire at 11:59 PM EST on the day they were placed.
                          </p>
                        </>
                      }
                    >
                      <span className='text-taupe-80 underline cursor-pointer'>Duration</span>
                    </Tooltip>
                    <span>{duration || ''}</span>
                  </div> */}
                </div>
              </div>
              {activeTab === '1' && (
                <>
                  <p className='fw-600 mt-32'>Payment details</p>
                  {!!isEditPayment ? (
                    <FloatingLabel label='Payment method' inputId='bankSelect' inputValue={selectedPaymentMethod.id}>
                      <div id='bank-anchor' />
                      <Form.Item className='mb-0' name='bankSelect'>
                        <Select
                          id='bankSelect'
                          onChange={(value) => {
                            value !== -1 ? handleSelectPaymentMethod(value) : setBankModalOpen(true);
                          }}
                          defaultValue={selectedPaymentMethod.id}
                          value={selectedPaymentMethod.id}
                          menuItemSelectedIcon={<CheckCircle verticalAlign='-8px' />}
                          getPopupContainer={() => document.getElementById('bank-anchor')}
                          onDropdownVisibleChange={() => setBankOpen(!bankOpen)}
                          suffixIcon={bankOpen ? <ChevronUp color='#1A1519' /> : <ChevronDown color='#1A1519' />}
                        >
                          {walletsData?.map((item) => (
                            <Select.Option value={item.id}>
                              {getBankLogo(item?.brandName)} {item?.brandName === 'DWOLLA' ? 'Realbricks Wallet' : item?.brandName || ''}{' '}
                              {item?.sourceType !== 'Wallet' && item?.sourceType !== 'add new' && `*** ${item.lastFourDigit || ''}`}
                              {item?.sourceType === 'Wallet' && '$'}
                              {item?.sourceType === 'Wallet' ? (
                                totalPurchase > item?.availableBalance ? (
                                  <span className='error-text'>Insufficient balance</span>
                                ) : (
                                  <span>{`${
                                    getUsCurrencyFormat(item?.availableBalance) || ''
                                  }`}</span>
                                )
                              ) : (
                                <span>{`${
                                  getUsCurrencyFormat(item?.availableBalance) || ''
                                }`}</span>
                              )}
                            </Select.Option>
                          ))}
                        </Select>
                        {selectedPaymentMethod?.sourceType === 'Wallet' && totalPurchase > selectedPaymentMethod?.availableBalance && (
                          <div className='pt-10 error-text'>
                            <span
                              style={{ cursor: 'pointer' }}
                              role='button'
                              tabIndex={0}
                              onClick={openLink}
                              onKeyDown={handleKeyActivate(openLink)}
                            >
                              Please go to {<a href={linkPath}>{linkText}</a>} to transfer funds into your account.
                            </span>
                          </div>
                        )}
                      </Form.Item>
                    </FloatingLabel>
                  ) : (
                    <>
                      <div className='d-flex g-8 align-items-center'>
                        {getBankLogo(selectedPaymentMethod?.brandName)}
                        <span className='fz-16'>
                          {selectedPaymentMethod?.brandName === 'DWOLLA' ? 'Realbricks Wallet' : selectedPaymentMethod?.brandName || ''}
                        </span>
                        {selectedPaymentMethod?.sourceType !== 'Wallet' && (
                          <span className='text-taupe-80 fz-16'>{`*** ${selectedPaymentMethod?.lastFourDigit || ''}`}</span>
                        )}
                        {selectedPaymentMethod?.sourceType === 'Wallet' &&
                        selectedPaymentMethod?.availableBalance !== undefined &&
                        selectedPaymentMethod?.availableBalance !== null ? (
                          totalPurchase > selectedPaymentMethod?.availableBalance ? (
                            <span className='text-taupe-80 fz-16 error-text'>Insufficient balance</span>
                          ) : (
                            <span className='text-taupe-80 fz-16'>{`$${getUsCurrencyFormat(selectedPaymentMethod?.availableBalance)}`}</span>
                          )
                        ) : null}
                      </div>
                      {selectedPaymentMethod?.sourceType === 'Wallet' && totalPurchase > selectedPaymentMethod?.availableBalance && (
                        <div className='pt-10 error-text'>
                          <span
                            style={{ cursor: 'pointer' }}
                            role='button'
                            tabIndex={0}
                            onClick={openLink}
                            onKeyDown={handleKeyActivate(openLink)}
                          >
                            Please go to {<a href={linkPath}>{linkText}</a>} to transfer funds into your account.
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              <div className='d-flex mt-48'>
                <Checkbox checked={isTurmAndCondistion} onChange={(value) => setIsTurmAndCondistion(value.target.checked)} className='mr-16' />
                <span className='cursor-pointer fz-14'>
                  <span
                    role='button'
                    tabIndex={0}
                    onClick={() => setIsTurmAndCondistion(!isTurmAndCondistion)}
                    onKeyDown={handleKeyActivate(() => setIsTurmAndCondistion(!isTurmAndCondistion))}
                  >
                    I have read and accept the{' '}
                  </span>
                  <Link to={{ pathname: '/legal', hash: 'Terms of use' }} className='underline' target='_blank'>
                    Terms of Use
                  </Link>
                  <span
                    role='button'
                    tabIndex={0}
                    onClick={() => setIsTurmAndCondistion(!isTurmAndCondistion)}
                    onKeyDown={handleKeyActivate(() => setIsTurmAndCondistion(!isTurmAndCondistion))}
                  >
                    , the{' '}
                  </span>
                  <Link to={{ pathname: '/legal', hash: 'Other disclosure here' }} className='underline' target='_blank'>
                    Regulation A Investment Disclosure
                  </Link>
                  <span
                    role='button'
                    tabIndex={0}
                    onClick={() => setIsTurmAndCondistion(!isTurmAndCondistion)}
                    onKeyDown={handleKeyActivate(() => setIsTurmAndCondistion(!isTurmAndCondistion))}
                  >
                    , and{' '}
                  </span>
                  <Link to={{ pathname: '/legal', hash: 'Privacy Policy' }} className='underline' target='_blank'>
                    Privacy Policy
                  </Link>
                  .
                </span>
              </div>
            </>
          ) : (
            <>
              <h4 className='text-center mb-24'>Review sell order</h4>
              <p className='fw-600'>Order details</p>
              <div className='d-flex flex-column g-16 fz-14'>
                <div className='d-flex align-items-center justify-content-space-between'>
                  <span className='text-taupe-80'>Shares</span>
                  <span>{numberOfShare || ''}</span>
                </div>
                <div className=' d-flex align-items-center justify-content-space-between'>
                  <span className='text-taupe-80'>Price per share</span>
                  <span>{`$${getUsCurrencyFormat(limitPrice || '')}`}</span>
                  {/* <span>{`$${limitPrice || ''}`}</span> */}
                </div>
                <div className=' d-flex align-items-center justify-content-space-between'>
                  <span className='text-taupe-80'>Estimated Credit</span>
                  <span>{`$${getUsCurrencyFormat(totalInvestment || '')}`}</span>
                </div>
                <div className=' d-flex align-items-center justify-content-space-between'>
                  <span className='text-taupe-80'>Platform fee </span>
                  <span>{`$2.50`}</span>
                </div>
                <div className=' d-flex align-items-center justify-content-space-between'>
                  <span className='text-taupe-80'>Broker-Dealer/ATS Fee (1%)</span>
                  <span>{`$${transactionFee || ''}`}</span>
                </div>
              </div>
              <hr className='mb-16 mt-16' />
              <div className='d-flex flex-column g-16 fz-14'>
                <div className='d-flex flex-column g-16 fz-14'>
                  <div className='d-flex align-items-center justify-content-space-between'>
                    <span className='text-taupe-80'>Estimated Net Credit</span>
                    <span className='fw-600'>{`$${getUsCurrencyFormat(totalPurchase || '')}`}</span>
                  </div>
                  {/* Duration field commented out - Good Until Cancel / Good for the Day */}
                  {/*                   <div className='d-flex align-items-center justify-content-space-between'>
                    <Tooltip
                      title={
                        <>
                          <p>
                            A “good until cancel” order is a buy or sell order that remains active until it is either executed or until the investor
                            cancels it.
                          </p>
                          <p className='mb-0'>
                            A “good for the day” order is a buy or sell order that is only good for the remainder of the trading day on which it is
                            placed. All “good for the day” orders will expire at 11:59 PM EST on the day they were placed.
                          </p>
                        </>
                      }
                    >
                      <span className='text-taupe-80 underline cursor-pointer'>Duration</span>
                    </Tooltip>
                    <span>{duration || ''}</span>
                  </div> */}
                </div>
              </div>
              {activeTab === '1' && (
                <>
                  <p className='fw-600 mt-32'>Payment details</p>
                  {!!isEditPayment ? (
                    <FloatingLabel label='Payment method' inputId='bankSelect' inputValue={selectedPaymentMethod.id}>
                      <div id='bank-anchor' />
                      <Form.Item className='mb-0' name='bankSelect'>
                        <Select
                          id='bankSelect'
                          onChange={(value) => {
                            value !== -1 ? handleSelectPaymentMethod(value) : setBankModalOpen(true);
                          }}
                          defaultValue={selectedPaymentMethod.id}
                          value={selectedPaymentMethod.id}
                          menuItemSelectedIcon={<CheckCircle verticalAlign='-8px' />}
                          getPopupContainer={() => document.getElementById('bank-anchor')}
                          onDropdownVisibleChange={() => setBankOpen(!bankOpen)}
                          suffixIcon={bankOpen ? <ChevronUp color='#1A1519' /> : <ChevronDown color='#1A1519' />}
                        >
                          {walletsData?.map((item) => (
                            <Select.Option value={item.id}>
                              {getBankLogo(item?.brandName)} {item?.brandName === 'DWOLLA' ? 'Realbricks Wallet' : item?.brandName}{' '}
                              {item?.sourceType !== 'Wallet' && item?.sourceType !== 'add new' && `*** ${item?.lastFourDigit || ''}`}
                              {item?.sourceType === 'Wallet' && '$'}
                              {item?.sourceType === 'Wallet' ? (
                                totalPurchase > item?.availableBalance ? (
                                  <span className='error-text'>Insufficient balance</span>
                                ) : (
                                  <span>{`${getUsCurrencyFormat(item?.availableBalance) || ''}`}</span>
                                )
                              ) : null}
                            </Select.Option>
                          ))}
                        </Select>
                        {selectedPaymentMethod?.sourceType === 'Wallet' && totalPurchase > selectedPaymentMethod?.availableBalance && (
                          <div className='pt-10 error-text'>
                            <span
                              style={{ cursor: 'pointer' }}
                              role='button'
                              tabIndex={0}
                              onClick={openLink}
                              onKeyDown={handleKeyActivate(openLink)}
                            >
                              Please go to {<a href={linkPath}>{linkText}</a>} to transfer funds into your account.
                            </span>
                          </div>
                        )}
                      </Form.Item>
                    </FloatingLabel>
                  ) : (
                    <>
                      <div className='d-flex g-8 align-items-center'>
                        {getBankLogo(selectedPaymentMethod?.brandName || '')}
                        <span className='fz-16'>{selectedPaymentMethod?.brandName === 'DWOLLA' ? 'Realbricks Wallet' : selectedPaymentMethod?.brandName || ''}</span>
                        {selectedPaymentMethod?.sourceType !== 'Wallet' && (
                          <span className='text-taupe-80 fz-16'>{`*** ${selectedPaymentMethod?.lastFourDigit || ''}`}</span>
                        )}
                        {selectedPaymentMethod?.sourceType === 'Wallet' &&
                        selectedPaymentMethod?.availableBalance !== undefined &&
                        selectedPaymentMethod?.availableBalance !== null ? (
                          totalPurchase > selectedPaymentMethod?.availableBalance ? (
                            <span className='text-taupe-80 fz-16 error-text'>Insufficient balance</span>
                          ) : (
                            <span className='text-taupe-80 fz-16'>{`$${getUsCurrencyFormat(selectedPaymentMethod?.availableBalance)}`}</span>
                          )
                        ) : null}
                      </div>
                      {selectedPaymentMethod?.sourceType === 'Wallet' && totalPurchase > selectedPaymentMethod?.availableBalance && (
                        <div className='pt-10 error-text'>
                          <span
                            style={{ cursor: 'pointer' }}
                            role='button'
                            tabIndex={0}
                            onClick={openLink}
                            onKeyDown={handleKeyActivate(openLink)}
                          >
                            Please go to {<a href={linkPath}>{linkText}</a>} to transfer funds into your account.
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              <div className='d-flex mt-48'>
                <Checkbox checked={isTurmAndCondistion} onChange={(value) => setIsTurmAndCondistion(value.target.checked)} className='mr-16' />
                <span className='cursor-pointer fz-14'>
                  <span
                    role='button'
                    tabIndex={0}
                    onClick={() => setIsTurmAndCondistion(!isTurmAndCondistion)}
                    onKeyDown={handleKeyActivate(() => setIsTurmAndCondistion(!isTurmAndCondistion))}
                  >
                    I have read and accept the{' '}
                  </span>
                  <Link to={{ pathname: '/legal', hash: 'Terms of use' }} className='underline' target='_blank'>
                    Terms of Use
                  </Link>
                  <span
                    role='button'
                    tabIndex={0}
                    onClick={() => setIsTurmAndCondistion(!isTurmAndCondistion)}
                    onKeyDown={handleKeyActivate(() => setIsTurmAndCondistion(!isTurmAndCondistion))}
                  >
                    , the{' '}
                  </span>
                  <Link to={{ pathname: '/legal', hash: 'Other disclosure here' }} className='underline' target='_blank'>
                    Regulation A Investment Disclosure
                  </Link>
                  <span
                    role='button'
                    tabIndex={0}
                    onClick={() => setIsTurmAndCondistion(!isTurmAndCondistion)}
                    onKeyDown={handleKeyActivate(() => setIsTurmAndCondistion(!isTurmAndCondistion))}
                  >
                    , and{' '}
                  </span>
                  <Link to={{ pathname: '/legal', hash: 'Privacy Policy' }} className='underline' target='_blank'>
                    Privacy Policy
                  </Link>
                  .
                </span>
              </div>
            </>
          )}
        </Modal>
      )}
      {screenMD && (
        <SuccessModal
          title={activeTab === '2' ? SellSuccessData?.title : successData?.title || ''}
          description={
            activeTab === '2'
              ? `You have successfully placed a sell order for ${numberOfShare || ''} shares ($${getUsCurrencyFormat(perSharePrice || 0)} each) of ${
                  quickBuyPropertyData?.propertyName
                }. You will be notified through email about any progress made toward your sell order.
            `
              : `You have successfully requested to buy ${numberOfShare || ''} shares ($${getUsCurrencyFormat(perSharePrice || 0)} each) of ${
                  quickBuyPropertyData?.propertyName
                }. You will be notified through email about any progress made toward your buy order.`
          }
          setOpen={props.open === 'success'}
          setClose={() => featchData()}
          setDone={() => {
            featchData();
          }}
        />
      )}
      {isTOSModal && <ListingAgreement isOpen={isTOSModal} onClose={onTOSModalClose} onAgreementAccept={onTOSAgreementSuccess} />}
    </>
  );
};

export default TradeModal;
